import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuthStore } from '../stores/auth.store';
import { NavBar } from '../components/NavBar';
import { toast } from '../stores/toast.store';

type ApiService = 'heygen' | 'openai' | 'brave_search';
type Platform = 'instagram' | 'tiktok' | 'youtube' | 'linkedin' | 'linkedin-page' | 'facebook' | 'telegram' | 'postproxy';

interface ApiKeyStatus { service: string; isValid: boolean; lastVerified: string | null }
interface SocialConn {
  platform: Platform;
  accountName: string | null;
  channelId: string | null;
  isActive: boolean;
  expiresAt: string | null;
  lastUsedAt: string | null;
}
interface PpProfile { id: string; name: string; language: string }
interface PostproxyStatus {
  healthy: boolean;
  usePostproxy: boolean;
  profileGroupId: string | null;
  profiles: Record<string, PpProfile[]>;
  dashboardUrl: string;
}

// Platforms connectable through PostProxy's on-board OAuth (initialize_connection).
const PP_PLATFORMS: Array<{ key: string; label: string; icon: string; color: string }> = [
  { key: 'instagram', label: 'Instagram', icon: '📸', color: 'from-pink-500 to-rose-500' },
  { key: 'youtube',   label: 'YouTube',   icon: '▶️', color: 'from-red-500 to-red-700' },
  { key: 'linkedin',  label: 'LinkedIn',  icon: '💼', color: 'from-blue-600 to-blue-800' },
  { key: 'facebook',  label: 'Facebook',  icon: '👥', color: 'from-blue-500 to-indigo-600' },
  { key: 'tiktok',    label: 'TikTok',    icon: '🎵', color: 'from-slate-700 to-slate-900' },
];

const API_SERVICES: Array<{ key: ApiService; label: string; hint: string; icon: string }> = [
  { key: 'heygen',       label: 'HeyGen',        icon: '🎬', hint: 'Генерация видео с AI-аватарами · app.heygen.com → Account → API' },
  { key: 'openai',       label: 'OpenAI',        icon: '🤖', hint: 'Генерация скриптов + эмбеддинги KB · platform.openai.com' },
  { key: 'brave_search', label: 'Brave Search',  icon: '🔎', hint: 'Веб-поиск для KB и конкурентных хуков · brave.com/search/api (free tier 2000/мес)' },
];

const SOCIAL_OAUTH: Array<{ key: 'instagram'|'youtube'|'tiktok'|'linkedin'; label: string; icon: string; color: string; hint: string }> = [
  { key: 'instagram', label: 'Instagram', icon: '📸', color: 'from-pink-500 to-rose-500',  hint: 'Reels через Meta Graph API. Нужно зарегистрировать app в Meta for Developers.' },
  { key: 'youtube',   label: 'YouTube',   icon: '▶️', color: 'from-red-500 to-red-700',    hint: 'Upload Shorts/Video через YouTube Data API v3 (Google Cloud Console).' },
  { key: 'tiktok',    label: 'TikTok',    icon: '🎵', color: 'from-slate-700 to-slate-900', hint: 'Direct Post API v2. Нужен dev-аккаунт TikTok.' },
  { key: 'linkedin',  label: 'LinkedIn',  icon: '💼', color: 'from-blue-600 to-blue-800',  hint: 'UGC Post API v2. Нужен dev-аккаунт LinkedIn.' },
];

export default function Settings() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();

  const [apiKeys, setApiKeys] = useState<ApiKeyStatus[]>([]);
  const [form, setForm] = useState<Record<ApiService, string>>({ heygen: '', openai: '', brave_search: '' });
  const [loading, setLoading] = useState<string | null>(null);

  // Social
  const [socials, setSocials] = useState<SocialConn[]>([]);
  const [tgBotToken, setTgBotToken] = useState('');
  const [tgChannel, setTgChannel] = useState('');
  const [ppStatus, setPpStatus] = useState<PostproxyStatus | null>(null);

  const reloadAll = async () => {
    const [keys, social, pp] = await Promise.all([
      api.get('/settings').catch(() => ({ data: { apiKeys: [] } })),
      api.get('/settings/social').catch(() => ({ data: { connections: [] } })),
      api.get('/settings/social/postproxy/status').catch(() => ({ data: null })),
    ]);
    setApiKeys(keys.data.apiKeys ?? []);
    setSocials(social.data.connections ?? []);
    setPpStatus(pp.data ?? null);
  };

  // On-board OAuth: ask backend for a PostProxy auth URL, then redirect the user.
  // They authorize on the social platform and return to /settings?pp_connected=…
  async function connectPostproxy(platform: string) {
    setLoading(`pp-${platform}`);
    try {
      const r = await api.post('/settings/social/postproxy/connect-init', { platform });
      if (r.data?.url) { window.location.href = r.data.url; return; }
      toast.error('PostProxy не вернул ссылку авторизации');
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'Ошибка');
    } finally { setLoading(null); }
  }

  async function resyncPostproxy() {
    setLoading('pp-resync');
    try {
      const r = await api.post('/settings/social/postproxy/resync');
      setPpStatus((s) => (s ? { ...s, profiles: r.data.profiles ?? {} } : s));
      toast.success('Профили PostProxy обновлены');
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'Ошибка');
    } finally { setLoading(null); }
  }

  useEffect(() => {
    if (!user) { navigate('/login', { replace: true }); return; }
    reloadAll();
    // Returned from a PostProxy on-board OAuth redirect → refresh the profile list.
    const q = new URLSearchParams(window.location.search);
    const connected = q.get('pp_connected');
    if (connected) {
      window.history.replaceState({}, '', '/settings');
      resyncPostproxy().then(() => toast.success(`${connected} подключён через PostProxy`));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  async function saveKey(service: ApiService) {
    const apiKey = form[service];
    if (!apiKey) return;
    setLoading(service);
    try {
      const { data } = await api.post('/settings/api-keys', { service, apiKey });
      toast.success(`${service}: ${data.message}`);
      setForm({ ...form, [service]: '' });
      await reloadAll();
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'Ошибка');
    } finally { setLoading(null); }
  }

  async function connectOAuth(platform: 'instagram'|'youtube'|'tiktok'|'linkedin') {
    setLoading(platform);
    try {
      const r = await api.get(`/settings/social/${platform}/connect`);
      if (r.data.authUrl) {
        // Открываем OAuth-окно в новой вкладке. После callback возвращаемся через redirect.
        window.open(r.data.authUrl, '_blank', 'width=600,height=700');
        toast.info(`Открылось окно авторизации ${platform}`);
      }
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'Ошибка');
    } finally { setLoading(null); }
  }

  async function connectTelegram() {
    if (!tgBotToken || !tgChannel) return;
    setLoading('telegram');
    try {
      const r = await api.post('/settings/social/telegram/connect', {
        botToken: tgBotToken,
        channelId: tgChannel,
      });
      toast.success(`Telegram подключён: ${r.data.accountName}`);
      setTgBotToken(''); setTgChannel('');
      await reloadAll();
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'Ошибка');
    } finally { setLoading(null); }
  }

  async function disconnect(platform: Platform) {
    if (!confirm(`Отключить ${platform}?`)) return;
    await api.delete(`/settings/social/${platform}`);
    toast.info(`${platform} отключён`);
    await reloadAll();
  }

  const keyFor = (s: string) => apiKeys.find((k) => k.service === s);
  const socialFor = (p: Platform) => socials.find((s) => s.platform === p && s.isActive);
  const tg = socialFor('telegram');

  return (
    <div className="min-h-screen lg:pl-64">
      <NavBar />
      <div className="max-w-3xl mx-auto px-4 md:px-6 pt-6 pb-16 space-y-6">
        <header>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Настройки</h1>
          <p className="text-slate-500 mt-1.5">{user?.email} · {user?.role}</p>
        </header>

        {/* === API КЛЮЧИ === */}
        <section className="card-glass p-6">
          <div className="flex items-start gap-4 mb-4 pb-4 border-b border-slate-100">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-2xl shadow-lg">
              🔐
            </div>
            <div>
              <h2 className="text-lg font-semibold">API ключи</h2>
              <p className="text-sm text-slate-500 mt-1">Шифруются AES-256 перед записью в БД.</p>
            </div>
          </div>
          {API_SERVICES.map((svc) => {
            const status = keyFor(svc.key);
            return (
              <div key={svc.key} className="py-4 border-t first:border-t-0 border-slate-100">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{svc.icon}</span>
                    <div>
                      <div className="font-semibold">{svc.label}</div>
                      <div className="text-xs text-slate-500">{svc.hint}</div>
                    </div>
                  </div>
                  {status && (
                    <span className={`badge ${status.isValid ? 'badge-green' : 'badge-amber'}`}>
                      {status.isValid ? '✓ Проверен' : '✗ Не прошёл'}
                    </span>
                  )}
                </div>
                <div className="flex gap-2 mt-3">
                  <input
                    type="password"
                    value={form[svc.key]}
                    onChange={(e) => setForm({ ...form, [svc.key]: e.target.value })}
                    placeholder={status ? 'Обновить ключ…' : 'Вставь API ключ'}
                    className="input flex-1 font-mono text-xs"
                  />
                  <button
                    onClick={() => saveKey(svc.key)}
                    disabled={loading === svc.key || !form[svc.key]}
                    className="btn btn-md btn-primary"
                  >
                    {loading === svc.key ? '⏳' : '✓ Проверить'}
                  </button>
                </div>
              </div>
            );
          })}
        </section>

        {/* === HEYGEN ДЕФОЛТЫ (аватар + голос) === */}
        <HeyGenDefaultsSection apiKeys={apiKeys} reloadAll={reloadAll} />

        {/* === POSTPROXY — единый агрегатор === */}
        <section className="card-glass p-6">
          <div className="flex items-start gap-4 mb-4 pb-4 border-b border-slate-100">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-2xl shadow-lg">
              ⚡
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg font-semibold">PostProxy · единый агрегатор</h2>
                <span className="badge badge-green">рекомендуется</span>
                {ppStatus?.healthy
                  ? <span className="badge badge-green">✓ на связи</span>
                  : <span className="badge badge-amber">offline</span>}
              </div>
              <p className="text-sm text-slate-500 mt-1">
                Все соцсети через одну API под единым авторизатором. Авторизация — прямо здесь: нажми «Подключить», авторизуйся в соцсети и вернись назад.
              </p>
            </div>
          </div>

          <div className="space-y-2">
            {PP_PLATFORMS.map((sn) => {
              const profiles = ppStatus?.profiles?.[sn.key] ?? [];
              const busy = loading === `pp-${sn.key}`;
              return (
                <div key={sn.key} className="py-3 border-t first:border-t-0 border-slate-100">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${sn.color} flex items-center justify-center text-white shrink-0 shadow-md`}>
                        {sn.icon}
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold flex items-center gap-2">
                          {sn.label}
                          {profiles.length > 0 && <span className="badge badge-green">✓ {profiles.length}</span>}
                        </div>
                        <div className="text-xs text-slate-500 truncate">
                          {profiles.length > 0
                            ? profiles.map((p) => `${p.name}${p.language !== 'unknown' ? ` (${p.language})` : ''}`).join(', ')
                            : 'не подключено'}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => connectPostproxy(sn.key)}
                      disabled={busy}
                      className="btn btn-sm btn-primary shrink-0"
                    >
                      {busy ? '⏳' : profiles.length > 0 ? '+ Ещё аккаунт' : '+ Подключить'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-100">
            <button onClick={resyncPostproxy} disabled={loading === 'pp-resync'} className="btn btn-sm btn-ghost">
              {loading === 'pp-resync' ? '⏳ Обновляю…' : '🔄 Обновить профили'}
            </button>
            <a href={ppStatus?.dashboardUrl ?? 'https://app.postproxy.dev'} target="_blank" rel="noreferrer" className="btn btn-sm btn-ghost">
              Открыть PostProxy ↗
            </a>
            <span className="text-xs text-slate-400 ml-auto">
              🇺🇦/🇷🇺 в имени профиля → авто-маршрутизация по языку темы
            </span>
          </div>
        </section>

        {/* === ОТДЕЛЬНЫЕ СОЦСЕТИ === */}
        {/* Direct OAuth section — only visible to PLATFORM ADMIN (owns Meta/Google/LinkedIn dev-apps).
            Tenants (role=user) connect socials exclusively via PostProxy (the block above) — that's
            the SaaS-correct flow where PostProxy acts as OAuth intermediary for any tenant. */}
        {user?.role === 'admin' && (
        <section className="card-glass p-6">
          <div className="flex items-start gap-4 mb-4 pb-4 border-b border-slate-100">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center text-2xl shadow-lg">
              🌐
            </div>
            <div>
              <h2 className="text-lg font-semibold">Соцсети напрямую <span className="text-xs font-normal text-slate-400">(admin only)</span></h2>
              <p className="text-sm text-slate-500 mt-1">
                Альтернатива PostProxy: подключить каждую сеть отдельно (требует своих OAuth dev-app для Meta/Google/TikTok/LinkedIn). Для tenant-ов используется только блок PostProxy выше.
              </p>
            </div>
          </div>

          {/* OAuth platforms */}
          {SOCIAL_OAUTH.map((sn) => {
            const conn = socialFor(sn.key);
            return (
              <div key={sn.key} className="py-4 border-t first:border-t-0 border-slate-100">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${sn.color} flex items-center justify-center text-white shrink-0 shadow-md`}>
                      {sn.icon}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold flex items-center gap-2">
                        {sn.label}
                        {conn && <span className="badge badge-green">✓ Подключён</span>}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {conn ? (conn.accountName ?? 'аккаунт без имени') : sn.hint}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {conn ? (
                      <button onClick={() => disconnect(sn.key)} className="btn btn-sm btn-ghost text-rose-600">
                        Отключить
                      </button>
                    ) : (
                      <button onClick={() => connectOAuth(sn.key)} disabled={loading === sn.key} className="btn btn-sm btn-primary">
                        {loading === sn.key ? '⏳' : '+ Подключить'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Telegram (отдельный flow) */}
          <div className="py-4 border-t border-slate-100">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-400 to-sky-600 flex items-center justify-center text-white shrink-0 shadow-md">
                ✈️
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold flex items-center gap-2">
                  Telegram
                  {tg && <span className="badge badge-green">✓ {tg.accountName ?? tg.channelId}</span>}
                </div>
                <div className="text-xs text-slate-500">
                  Bot Token (BotFather → /newbot) + Channel ID или @username канала. Бот должен быть админом канала.
                </div>
              </div>
              {tg && (
                <button onClick={() => disconnect('telegram')} className="btn btn-sm btn-ghost text-rose-600 shrink-0">
                  Отключить
                </button>
              )}
            </div>
            {!tg && (
              <div className="space-y-2 pl-13 ml-13" style={{ paddingLeft: '3.25rem' }}>
                <input
                  type="password"
                  value={tgBotToken}
                  onChange={(e) => setTgBotToken(e.target.value)}
                  placeholder="Bot Token (123456:AAAA-...)"
                  className="input font-mono text-xs"
                />
                <div className="flex gap-2">
                  <input
                    value={tgChannel}
                    onChange={(e) => setTgChannel(e.target.value)}
                    placeholder="@username_канала или -1001234567890"
                    className="input flex-1"
                  />
                  <button
                    onClick={connectTelegram}
                    disabled={loading === 'telegram' || !tgBotToken || !tgChannel}
                    className="btn btn-md btn-primary"
                  >
                    {loading === 'telegram' ? '⏳' : '✓ Подключить'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>
        )}

        <p className="text-xs text-slate-400 text-center pt-2">
          OAuth-токены и bot tokens шифруются AES-256-GCM. Refresh-токены обновляются автоматически каждые 6 часов.
        </p>
      </div>
    </div>
  );
}

interface HeyGenAvatar {
  avatar_id: string;
  avatar_name: string;
  gender?: string;
  preview_image_url?: string;
  preview_video_url?: string;
}
interface HeyGenVoice  { voice_id: string;  name?: string; language?: string; gender?: string; preview_audio?: string }

function HeyGenDefaultsSection({ apiKeys, reloadAll }: { apiKeys: ApiKeyStatus[]; reloadAll: () => Promise<void> }) {
  const heygenKey = apiKeys.find((k) => k.service === 'heygen' && k.isValid);

  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [avatars, setAvatars]   = useState<HeyGenAvatar[] | null>(null);
  const [voices, setVoices]     = useState<HeyGenVoice[] | null>(null);
  const [pickedAvatar, setPickedAvatar] = useState<string>('');
  const [pickedVoice, setPickedVoice]   = useState<string>('');
  const [filter, setFilter]     = useState('');

  // Load current saved defaults so user sees what's already chosen
  useEffect(() => {
    api.get('/settings').then((r) => {
      const s = r.data.settings ?? {};
      setPickedAvatar(s.defaultAvatarIds?.[0] ?? '');
      setPickedVoice(s.defaultVoiceId ?? '');
    }).catch(() => { /* settings page already shows the error */ });
  }, []);

  async function loadCatalog() {
    setLoading(true);
    try {
      const [a, v] = await Promise.all([
        api.get('/settings/heygen/avatars'),
        api.get('/settings/heygen/voices'),
      ]);
      setAvatars(a.data.avatars ?? []);
      setVoices(v.data.voices ?? []);
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'Не удалось загрузить каталог HeyGen');
    } finally { setLoading(false); }
  }

  async function save() {
    if (!pickedAvatar && !pickedVoice) {
      toast.error('Выберите аватар или голос');
      return;
    }
    setSaving(true);
    try {
      // Save whatever is picked — partial saves are allowed so the button always
      // works. Both are needed for video generation; warn if one is still missing.
      const patch: { defaultAvatarIds?: string[]; defaultVoiceId?: string } = {};
      if (pickedAvatar) patch.defaultAvatarIds = [pickedAvatar];
      if (pickedVoice) patch.defaultVoiceId = pickedVoice;
      await api.put('/settings', patch);
      const incomplete = !pickedAvatar || !pickedVoice;
      toast.success(
        incomplete
          ? 'Сохранено. Для генерации видео нужны и аватар, и голос — выберите второе.'
          : 'Сохранено: видео теперь будут идти с этим аватаром и голосом'
      );
      await reloadAll();
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'Ошибка');
    } finally { setSaving(false); }
  }

  const fA = (avatars ?? []).filter((a) => !filter || a.avatar_name.toLowerCase().includes(filter.toLowerCase()));
  const fV = (voices  ?? []).filter((v) => !filter || (v.name ?? '').toLowerCase().includes(filter.toLowerCase()) || (v.language ?? '').toLowerCase().includes(filter.toLowerCase()));

  return (
    <section className="card-glass p-6">
      <div className="flex items-start gap-4 mb-4 pb-4 border-b border-slate-100">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-fuchsia-500 to-purple-600 flex items-center justify-center text-2xl shadow-lg">
          🎭
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-semibold">HeyGen — аватар и голос по умолчанию</h2>
          <p className="text-sm text-slate-500 mt-1">
            Без этого видео не сгенерируются. Выберите один раз — будет применяться ко всем темам без явного override.
          </p>
        </div>
      </div>

      {!heygenKey && (
        <div className="text-sm bg-amber-50 text-amber-800 rounded-xl p-3">
          Сначала добавьте валидный HeyGen API ключ в секции выше.
        </div>
      )}

      {heygenKey && !avatars && (
        <button onClick={loadCatalog} disabled={loading} className="btn btn-primary">
          {loading ? 'Загружаю каталог HeyGen (~20с)…' : '📥 Загрузить мои аватары и голоса'}
        </button>
      )}

      {avatars && voices && (
        <>
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="🔎 Поиск по имени / языку…"
            className="input mb-4"
          />

          <div className="space-y-6">
            {/* AVATAR PICKER — image grid with preview thumbnails */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
                  Аватар ({fA.length} из {avatars.length})
                </div>
                {pickedAvatar && (
                  <div className="text-xs text-slate-500">
                    выбран: <code className="bg-slate-100 px-1.5 py-0.5 rounded">{pickedAvatar.slice(0, 8)}…</code>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 max-h-[420px] overflow-y-auto p-1 rounded-xl bg-slate-50/50">
                {fA.slice(0, 80).map((a) => {
                  const isPicked = a.avatar_id === pickedAvatar;
                  return (
                    <button
                      key={a.avatar_id}
                      type="button"
                      onClick={() => setPickedAvatar(a.avatar_id)}
                      title={`${a.avatar_name}\nID: ${a.avatar_id}`}
                      className={`group relative rounded-xl overflow-hidden ring-2 transition-all ${
                        isPicked ? 'ring-indigo-500 shadow-lg scale-[1.02]' : 'ring-transparent hover:ring-indigo-200'
                      }`}
                    >
                      {a.preview_image_url ? (
                        <img
                          src={a.preview_image_url}
                          alt={a.avatar_name}
                          loading="lazy"
                          className="w-full aspect-[3/4] object-cover bg-slate-200"
                          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-full aspect-[3/4] bg-slate-200 flex items-center justify-center text-3xl">🎭</div>
                      )}
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent text-white text-[10px] leading-tight px-1.5 py-1 text-left">
                        <div className="truncate font-medium">{a.avatar_name}</div>
                        <div className="opacity-70 truncate">{a.gender ?? '—'}</div>
                      </div>
                      {isPicked && (
                        <div className="absolute top-1 right-1 bg-indigo-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">✓</div>
                      )}
                    </button>
                  );
                })}
              </div>
              {fA.length > 80 && (
                <div className="text-xs text-slate-400 mt-1">показаны первые 80 — введите поиск чтобы найти конкретного</div>
              )}
            </div>

            {/* VOICE PICKER — list with audio preview */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
                  Голос ({fV.length} из {voices.length})
                </div>
                {pickedVoice && (
                  <div className="text-xs text-slate-500">
                    выбран: <code className="bg-slate-100 px-1.5 py-0.5 rounded">{pickedVoice.slice(0, 8)}…</code>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[420px] overflow-y-auto p-1 rounded-xl bg-slate-50/50">
                {fV.slice(0, 80).map((v) => {
                  const isPicked = v.voice_id === pickedVoice;
                  return (
                    <div
                      key={v.voice_id}
                      onClick={() => setPickedVoice(v.voice_id)}
                      className={`group flex items-center gap-2 p-2 rounded-xl bg-white ring-2 transition cursor-pointer ${
                        isPicked ? 'ring-indigo-500 shadow' : 'ring-transparent hover:ring-indigo-200'
                      }`}
                    >
                      <div className="text-2xl">{v.gender === 'female' ? '👩' : v.gender === 'male' ? '👨' : '🎙'}</div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate" title={v.voice_id}>{v.name ?? v.voice_id.slice(0,12)}</div>
                        <div className="text-xs text-slate-500 truncate">
                          {v.language ?? '—'} {v.gender ? `· ${v.gender}` : ''}
                        </div>
                      </div>
                      {v.preview_audio && (
                        <audio
                          controls
                          preload="none"
                          src={v.preview_audio}
                          onClick={(e) => e.stopPropagation()}
                          className="h-7"
                          style={{ maxWidth: 130 }}
                        />
                      )}
                      {isPicked && <span className="text-indigo-500 text-lg">✓</span>}
                    </div>
                  );
                })}
              </div>
              {fV.length > 80 && (
                <div className="text-xs text-slate-400 mt-1">показаны первые 80 — введите поиск чтобы найти конкретный</div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 mt-4">
            <button onClick={save} disabled={saving} className="btn btn-primary">
              {saving ? 'Сохраняю…' : '💾 Сохранить как дефолт'}
            </button>
            <button onClick={loadCatalog} disabled={loading} className="btn btn-ghost">
              🔄 Перезагрузить каталог
            </button>
            {(!pickedAvatar || !pickedVoice) && (
              <span className="text-xs text-amber-600">
                {!pickedAvatar && !pickedVoice
                  ? 'выберите аватар и голос'
                  : !pickedAvatar
                  ? 'аватар не выбран'
                  : 'голос не выбран'}
              </span>
            )}
          </div>
        </>
      )}
    </section>
  );
}
